import {
  Button,
  Flex,
  Grid,
  HStack,
  Heading,
  Spacer,
  VStack,
} from '@hover/blueprint';
import { hBack } from '@hover/icons';
import React from 'react';
import { useHistory } from 'react-router';
import type { InferType } from 'yup';

import { useOnboardingError } from 'src/modules/onboarding/hooks/useOnboardingError';
import { makeNameValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { Form as SharedForm, TextField } from 'src/shared/components/forms';
import { useUpdateUserMutation } from 'src/shared/graphql/generated';
import { useEffectOnMount, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';

export type UserNameFormData = InferType<
  ReturnType<typeof makeNameValidationSchema>
>;

interface NameFormData {
  firstName: string | undefined;
}
const UserNameFormFields = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const backAction = () => null;

  return (
    <>
      <HStack
        flexWrap="wrap"
        justifyContent={{ base: 'start', tablet: 'space-around' }}
        marginTop={300}
        spacing={0}
        width={1}
      >
        <Heading size={{ base: 700, tablet: 800 }} whiteSpace="pre">
          {t('onboarding.whatsYourName')}
        </Heading>
        <TextField
          name="firstName"
          placeholder={t('onboarding.firstName')}
          width={{ base: 1, tablet: 'auto' }}
        />
      </HStack>
      <Spacer flex={1} />
      <VStack width={1}>
        <Flex justifyContent="space-between" width={1}>
          <Button
            display={{ base: 'none', tablet: 'flex' }}
            fill="minimal"
            iconBefore={hBack}
            onClick={backAction}
          >
            {t('onboarding.back')}
          </Button>
          <Button type="submit" width={{ base: 1, tablet: 'fit-content' }}>
            {t('onboarding.next')}
          </Button>
        </Flex>
      </VStack>
    </>
  );
};

export const UserName = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const { logError } = useOnboardingError();
  const [updateUser] = useUpdateUserMutation();
  const validationSchema = makeNameValidationSchema(t);
  const initialValues = { firstName: '' };

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Onboarding-name_input',
      feature: 'onboarding',
    });
  });

  const onSubmit = async (formData: NameFormData) => {
    buttonPressed({
      button_text: 'Next',
      page_or_screen_name: 'Onboarding-name_input',
      feature: 'onboarding',
    });
    const { firstName } = formData;
    if (!firstName) return;

    try {
      await updateUser({
        variables: {
          firstName,
        },
      });
    } catch (e) {
      logError(e as Error);
    } finally {
      history.push(`${Routes.SIGN_UP_ONBOARDING.path}/persona`);
    }
  };

  return (
    <Grid alignItems="stretch" flex={1} width={1}>
      <SharedForm<UserNameFormData>
        display="flex"
        height="100%"
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <UserNameFormFields />
      </SharedForm>
    </Grid>
  );
};
